/**
 * SINGLE SOURCE OF TRUTH for the Treatment Planning tour (persisted under the
 * tour_name "insurance_verification" — see tourConfigs.js).
 * The engine, the JIT first-use listener, and persistence all key off `id`.
 * `selector` is the data-onboarding-id value (the engine resolves
 * `[data-onboarding-id="<selector>"]`). Add/reorder a step = edit this file only.
 *
 * Step: { id, title, body, selector|null, side, insideModal, jit, preActions[] }
 * preAction: { type:'click', selector } | { type:'clickFirstOption' } | { type:'wait', ms }
 */
export const TOUR_ROUTE = "/treatment-plans";

export const STEPS = [
  { id: "intro", title: "Welcome 👋", side: "center", selector: null,
    body: "This is Treatment Planning — where you pull and track breakdowns & estimates for every patient. Let me show you around in under 2 minutes." },

  { id: "tab-all-patients", title: "All Patients", selector: "tab-all-patients", side: "bottom",
    preActions: [{ type: "click", selector: "tab-all-patients" }],
    body: "Everyone in your practice lives here. The other tabs are focused filters." },
  { id: "tab-upcoming", title: "Upcoming Patients", selector: "tab-upcoming", side: "bottom",
    preActions: [{ type: "click", selector: "tab-upcoming" }],
    body: "Patients with upcoming appointments — verify their coverage before they arrive." },
  { id: "tab-fresh-breakdowns", title: "Fresh Breakdowns", selector: "tab-fresh-breakdowns", side: "bottom",
    preActions: [{ type: "click", selector: "tab-fresh-breakdowns" }],
    body: "Breakdowns that just came back and need a look. The badge counts what's waiting." },
  { id: "tab-new-estimates", title: "New Estimates", selector: "tab-new-estimates", side: "bottom",
    preActions: [{ type: "click", selector: "tab-new-estimates" }],
    body: "Newly available estimates/predeterminations to review." },

  { id: "patient-row", title: "A patient row", selector: "patient-row-demo", side: "right",
    preActions: [{ type: "click", selector: "tab-all-patients" }, { type: "wait", ms: 400 }],
    body: "Each row is one patient. Here's a demo patient so I can show you the controls — it won't appear in your real board." },
  { id: "row-note", title: "Notes", selector: "row-note-cell", side: "top",
    body: "A quick note about the patient (why they were flagged, anything to remember)." },
  { id: "row-activity-dates", title: "Latest activity", selector: "row-activity-cell", side: "top",
    body: "The dates of the most recent breakdown and estimate for this patient — at a glance." },

  { id: "row-tags", title: "Tags", selector: "row-tag-select", side: "top", jit: true,
    body: "Tag a patient to request work or mark its state (e.g. “Breakdown - Requested”)." },
  { id: "row-tag-note-popover", title: "Add a note", selector: "tag-note-popover", side: "left",
    preActions: [{ type: "openSelect", selector: "row-tag-select" }, { type: "wait", ms: 300 }, { type: "clickFirstOption" }, { type: "wait", ms: 450 }],
    body: "When you add a tag, this popup lets you attach a short note for context." },
  { id: "row-tag-note-save", title: "Save the note", selector: "tag-note-save", side: "left",
    body: "Save commits the tag + note to the patient." },

  { id: "row-policy-btn", title: "Policy", selector: "row-policy-btn", side: "top", jit: true,
    preActions: [{ type: "click", selector: "tag-note-cancel" }, { type: "wait", ms: 200 }],
    body: "Open the patient's insurance policy details — carrier, member ID, contract." },
  { id: "row-request-breakdown", title: "Request Breakdown", selector: "row-request-breakdown-btn", side: "top", jit: true,
    body: "Kick off a breakdown right from the row — instant AI check, a dispatched request, or urgent." },
  { id: "row-insurance-btn", title: "Insurance", selector: "row-insurance-btn", side: "top", jit: true,
    body: "This opens the full Insurance workspace — breakdowns & estimates. We'll go in next." },

  { id: "insurance-tab-breakdowns", title: "Breakdowns tab", selector: "insurance-tab-breakdowns", side: "bottom", insideModal: true, jit: true,
    preActions: [{ type: "click", selector: "row-insurance-btn" }, { type: "wait", ms: 600 }, { type: "click", selector: "insurance-tab-breakdowns" }, { type: "wait", ms: 300 }],
    body: "Coverage breakdowns — what the plan covers, by category." },
  { id: "insurance-tab-estimates", title: "Estimates tab", selector: "insurance-tab-estimates", side: "bottom", insideModal: true, jit: true,
    body: "Predetermination estimates live here — we'll come back to it." },

  { id: "bd-select-policy", title: "Select a policy", selector: "bd-select-policy", side: "right", insideModal: true, jit: true,
    preActions: [{ type: "click", selector: "insurance-tab-breakdowns" }, { type: "wait", ms: 400 }],
    body: "Pick which policy to work with (or add a new one) — everything below reflects it." },
  { id: "bd-policy-info", title: "Member, patient and plan", selector: "bd-policy-info", side: "bottom", insideModal: true,
    body: "Who the plan belongs to, who the patient is, and the plan's details. Use Edit plan to change any of them." },
  { id: "bd-finance-cards", title: "The breakdown", selector: "bd-finance-cards", side: "top", insideModal: true,
    body: "The heart of it: coverage by category — Preventative, Basic, Major and Orthodontics with their %, maximums and what's left — then the procedures below." },
  { id: "bd-notes", title: "Notes", selector: "bd-failure-card", side: "right", insideModal: true,
    body: "Context and attention notes for this breakdown." },
  { id: "bd-list", title: "Breakdowns list", selector: "bd-breakdowns-menu", side: "right", insideModal: true,
    body: "Every breakdown on file for this patient — click one to view it." },
  { id: "bd-add-manually", title: "Add Manually", selector: "bd-add-manually", side: "right", insideModal: true, jit: true,
    body: "Already have the coverage info? Enter a breakdown by hand." },
  { id: "bd-verify-action", title: "Run a verification", selector: "bd-verify-action", side: "right", insideModal: true, jit: true,
    body: "Pull a fresh breakdown — AI Verification when the carrier supports it, otherwise dispatch a request (mark it urgent if needed)." },

  { id: "est-overview", title: "Estimates", selector: "est-pane-right", side: "left", insideModal: true,
    preActions: [{ type: "click", selector: "insurance-tab-estimates" }, { type: "wait", ms: 400 }],
    body: "The estimate itself — its predetermination outcome, policy details and coverage. It mirrors the Breakdowns tab, but for estimates. (Pick a policy and browse history from the column on the left.)" },

  { id: "finish", title: "You're set 🎉", side: "center", selector: null,
    preActions: [{ type: "click", selector: "insurance-modal-close" }, { type: "wait", ms: 200 }],
    body: "That's the tour! You can replay it anytime from “Take the tour” in the top bar. Happy verifying." },
];

export const getStepByIndex = (i) => STEPS[i] || null;
export const getStepById = (id) => STEPS.find((s) => s.id === id) || null;
export const getStepBySelector = (sel) => STEPS.find((s) => s.selector === sel) || null;
export const TOTAL_STEPS = STEPS.length;
export const ALL_STEP_IDS = STEPS.map((s) => s.id);
